import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "add-client-modal" }
const _hoisted_2 = { class: "add-client-modal__search" }
const _hoisted_3 = {
  key: 0,
  class: "add-client-modal__close"
}
const _hoisted_4 = { class: "add-client-modal__header" }
const _hoisted_5 = { class: "add-client-modal__header-title" }
const _hoisted_6 = ["onClick", "innerHTML"]

import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import { PersonApi } from "@/ api/PersonApi";
import { CommonEntityRecord } from "@/models/interfaces/common";
import { Client, clientsStore } from "@/store/clients";
import { debounce } from "lodash";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { escapeHtml, highlightSearchText } from "@/utils/textHelpers";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

type AddedClient = {
	Id: number;
	Name: string;
	Address: string[]
}

const take = 60

export default /*@__PURE__*/_defineComponent({
  __name: 'ContactAddClientDropdown',
  props: {
    contactId: {},
    client: {},
    addedClients: {},
    showCloseIcon: { type: Boolean }
  },
  emits: ['close'],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const store = clientsStore()

const isLoading = ref(true);
const contactId = ref<number | null>(null)
const filter = ref('')
const appliedFilter = ref('')
const skip = ref(0)
const canLoadNext = ref(true)
const isClientsListInit = ref(false)
const clients = ref<AddedClient[] | null>(null)
const existClients = ref<AddedClient[]>([])

const filteredClients = computed(() => {
	if (!existClients.value.length) return clients.value

	const sortedClientsList = [...existClients.value]
	clients.value?.forEach(client => {
		if (!existClients.value.find(c => c.Id === client.Id)) {
			sortedClientsList.push(client)
		}
	})

	return sortedClientsList
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const getClientLabel = (client: AddedClient) => {
	const address = client.Address?.[0] ? `(${client.Address[0]})` : ''
	return highlightSearchText(appliedFilter.value, escapeHtml(`${client.Name ?? ''}${address}`))
}

const onOpen = async () => {
	try {
		existClients.value = props.addedClients.map(c => ({ ...c, Address: [''] }));
		contactId.value = props.contactId
		await onFetchClients()
		isClientsListInit.value = true
		await nextTick(() => $input.value?.focusInput())
	} catch (e) {
		console.error(e)
	}
}

const onSearch = async () => {
	$body.value?.scrollTo({
		top: 0
	});
	canLoadNext.value = true
	skip.value = 0
	await onFetchClients()
}

const debouncedSearch = debounce(onSearch, 500)

const onFetchClients = async () => {
	try {
		if (canLoadNext.value) {
			isLoading.value = true
			const requestedFilter = filter.value
			const clientsArray = await PersonApi.getAttachClientsList(
				contactId.value as number,
				requestedFilter,
				skip.value,
				take
			)
			appliedFilter.value = requestedFilter
			clients.value = skip.value === 0
				? clientsArray
				: clients.value
					? [...clients.value, ...clientsArray]
					: clientsArray
			skip.value += take

			if (clientsArray.length < take) {
				canLoadNext.value = false
			}
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onAddClientToContact = async (clientId: number) => {
	try {
		isLoading.value = true
		await store.attachClientToContact(props.client, clientId)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onDeleteClientFromContact = async (clientId: number) => {
	try {
		isLoading.value = true
		await store.detachClientFromContact(props.client, clientId)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const dropAllClients = async () => {
	try {
		isLoading.value = true
		await store.detachAllClientsFromContact(
			props.client,
			existClients.value.slice(1).map(client => client.Id),
			existClients.value[0].Id
		)
		existClients.value = props.addedClients.map(c => ({ ...c, Address: [''] }));
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const isClientAdded = (clientId: number) => {
	return !!existClients.value.find(client => client.Id === clientId)
}

const onChangeClientStage = async (stage: boolean, clientId: number) => {
	if (clients.value) {
		if (stage) {
			await onAddClientToContact(clientId)
			existClients.value.push(clients.value.find(client => client.Id === clientId)!)
		} else if (existClients.value.length > 1) {
			await onDeleteClientFromContact(clientId)
			existClients.value.splice(existClients.value.findIndex(client => client.Id === clientId), 1)
		}
	}
}

const onChangeClientStageFromName = async (clientId: number) => {
	if (clients.value) {
		if (existClients.value.find(client => client.Id === clientId)) {
			if (existClients.value.length > 1) {
				await onDeleteClientFromContact(clientId)
				existClients.value.splice(existClients.value.findIndex(client => client.Id === clientId), 1)
			}
		} else {
			await onAddClientToContact(clientId)
			existClients.value.push(clients.value.find(client => client.Id === clientId)!)
		}
	}
}

watch(filter, (newVal, oldVal) => {
	if (newVal !== oldVal) {
		debouncedSearch()
	}
})

watch(isClientsListInit, (newValue) => {
	if (newValue) {
		nextTick(() => {
			$body.value?.addEventListener('scroll', async () => {
				if (canLoadNext.value && !isLoading.value) {
					if ($body.value?.scrollTop! + $body.value?.offsetHeight! > ($body.value?.scrollHeight! - 500)) {
						await onFetchClients()
					}
				}
			})
		})
	}
})

onMounted(() => onOpen())

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(AppLoader, {
          key: 0,
          style: _normalizeStyle(loaderStyle.value)
        }, null, 8, ["style"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(AppInput, {
        ref_key: "$input",
        ref: $input,
        modelValue: filter.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filter).value = $event)),
        class: "add-client-modal__search-input",
        placeholder: _ctx.$t('search_short')
      }, null, 8, ["modelValue", "placeholder"]),
      (props.showCloseIcon)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(AppTooltip, {
              text: _ctx.$t('close_label')
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  class: "create-edit-contact__body-tags-item-delete",
                  name: "close",
                  width: "20px",
                  color: "#D2D2D2",
                  "hover-color": "#499259",
                  cursor: "pointer",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
                })
              ]),
              _: 1
            }, 8, ["text"])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t('clients_system')), 1),
      _createElementVNode("div", {
        class: "add-client-modal__header-drop-btn",
        onClick: dropAllClients
      }, _toDisplayString(_ctx.$t('selector_reset')), 1)
    ]),
    _createElementVNode("div", {
      class: "add-client-modal__body",
      ref_key: "$body",
      ref: $body
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredClients.value, (client) => {
        return (_openBlock(), _createElementBlock("div", {
          class: "add-client-modal__body-item",
          key: client.Id
        }, [
          _createVNode(AppCheckbox, {
            style: { height: '16px', marginRight: '8px' },
            value: isClientAdded(client.Id),
            text: "",
            disabled: isClientAdded(client.Id) && existClients.value.length === 1,
            onInput: ($event: any) => (onChangeClientStage($event, client.Id))
          }, null, 8, ["value", "disabled", "onInput"]),
          _createElementVNode("div", {
            class: "add-client-modal__body-item-text",
            onClick: ($event: any) => (onChangeClientStageFromName(client.Id)),
            innerHTML: getClientLabel(client)
          }, null, 8, _hoisted_6)
        ]))
      }), 128))
    ], 512)
  ]))
}
}

})